import { Search } from '@mui/icons-material';
import { Checkbox, ListItemText, MenuItem, TextField } from '@mui/material';
import debounce from 'lodash.debounce';
import { useState } from 'react';
import { TableFilter } from '../TableFilter';

export interface SearchableMultiSelectProps {
  id: string;
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: Array<{
    id: string;
    label: string;
  }>;
  isLoading?: boolean;
  onSearch?: (searchTerm: string) => void;
  searchPlaceholder?: string;
  className?: string;
}

export function SearchableMultiSelect({
  id,
  label,
  value,
  onChange,
  options,
  isLoading,
  onSearch,
  searchPlaceholder = 'Search...',
  className,
}: SearchableMultiSelectProps) {
  const [searchTerm, setSearchTerm] = useState('');

  return (
    <TableFilter
      className={className}
      id={id}
      label={label}
      isLoading={isLoading}
      value={value}
      multiple
      onChange={e => {
        if (e.target.value.includes('custom_search')) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        onChange(e.target.value as string[]);
      }}
      renderValue={selected => {
        if (!selected.length) {
          return 'All';
        }

        const selectedOptions = options.filter(option =>
          selected.includes(option.id),
        );

        if (selectedOptions.length > 1) {
          return `${selectedOptions[0]?.label || selected[0]} and ${
            selectedOptions.length - 1
          } more`;
        }

        return selectedOptions[0]?.label || selected[0];
      }}
      MenuProps={{
        PaperProps: {
          classes: {
            root: 'max-h-96',
          },
        },
      }}
    >
      <MenuItem
        className="top-0 w-full left-0 sticky !bg-white z-10 max-h-28"
        disableTouchRipple
        value="custom_search"
        onClickCapture={e => {
          e.stopPropagation();
        }}
        onPointerDownCapture={e => {
          e.stopPropagation();
        }}
        sx={{
          backgroundColor: 'transparent !important',
        }}
        classes={{
          root: 'p-2',
        }}
      >
        <TextField
          size="small"
          defaultValue={searchTerm}
          placeholder={searchPlaceholder}
          className="w-full"
          onKeyDownCapture={e => {
            if (!['ArrowUp', 'ArrowDown'].includes(e.key)) {
              e.stopPropagation();
            }
          }}
          onChange={debounce(e => {
            setSearchTerm(e.target.value);
            onSearch?.(e.target.value);
          }, 300)}
          slotProps={{
            input: {
              endAdornment: <Search />,
            },
          }}
        />
      </MenuItem>
      {options.map(option => (
        <MenuItem
          key={option.id}
          value={option.id}
          classes={{
            root: 'p-2',
          }}
        >
          <div className="flex gap-2 items-center capitalize">
            <Checkbox
              sx={{
                padding: 0,
                '&.Mui-checked': {
                  color: 'primary',
                },
              }}
              checked={value.includes(option.id)}
            />
            <ListItemText>{option.label}</ListItemText>
          </div>
        </MenuItem>
      ))}
    </TableFilter>
  );
}
